import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import defaultCoverImage from 'img/subscriptions/cancel-autorenew.jpg';
import trialExpiryCoverImage from 'img/subscriptions/cancel-autorenew-trial-expiry-reminder.jpg';
import vkMusicOutflowImage from 'img/subscriptions/vkmusic-outflow-dark-desktop.png';
import React, { type ReactElement, memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { autoUploadUnsubs } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { updatedUnsubScreensEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import { isPublicPasswordEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPassword';
import { isThemedPublicEnabled } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { trialExpiryReminder } from 'reactApp/appHelpers/featuresHelpers/features/trialExpiryReminder';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { SPACE_SIZES } from 'reactApp/modules/products/products.helpers';
import { isAutoDeleteEnabled } from 'reactApp/modules/sharedAutoDelete/sharedAutoDelete.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import {
    ESubscriptionModalAction,
    useSendSubscriptionModalAnalytics,
} from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import { sendXray } from 'reactApp/utils/ga';
import { daysNominativePlural, disappearPlural, disappearV2Plural } from 'reactApp/utils/pluralHelpers';
import { capitalizeFirstLetter } from 'reactApp/utils/string';

import styles from './CancelAutoRenewConfirmDialog.css';

const MILLISECONDS_IN_DAY = 60 * 60 * 24 * 1000;

export interface CancelAutoRenewConfirmDialogProps {
    onClose: () => void;
    onAction: () => void;
    onSuccess: () => void;
    onShow?: () => void;
    isTrial: boolean;
    isVkMusic: boolean;
    subscription: BillingDataSubscriptionInfo;
    skipAnalytic?: boolean;
}

type Variant = (props: { isTrial?: boolean; value?: string; daysLeft?: number; features?: SubscriptionFeatures }) => {
    image: string;
    title: string;
    subtitle: string;
    body: string[] | string;
};

type Variants = {
    [key: string]: Variant;
};

export interface SubscriptionFeatures {
    family: boolean;
    unlimitedUpload: boolean;
    disableAds: boolean;
    autodelete: boolean;
    themedPublic: boolean;
    publicPassword: boolean;
}

export const useGetPaidFeatures = () => {
    const billingFeatures = useSelector(BillingDataSelectors.getSubscriptionFeatures);
    const autodelete = useSelector(isAutoDeleteEnabled);
    const isPaid = useSelector(UserSelectors.isPaidUser);
    return {
        ...billingFeatures,
        autodelete,
        themedPublic: isThemedPublicEnabled && isPaid,
        publicPassword: isPublicPasswordEnabled,
    };
};

const defaultVariant: Variant = ({ value, isTrial }) => ({
    image: defaultCoverImage,
    // eslint-disable-next-line sonarjs/no-duplicate-string
    title: 'Вы уверены?',
    subtitle: `После ${isTrial ? 'отмены' : 'окончания'} подписки:`,
    body: [`Объём единого хранилища уменьшится на ${value}`, 'Не получится загружать тяжёлые файлы', 'Вернётся реклама'],
});

const disappearPluralByValue = (value?: string) => disappearPlural(parseInt(value || '0'));
const disappearPluralV2ByValue = (value?: string) => disappearV2Plural(parseInt(value || '0'));
const disappearPluralByValueCapitalize = (value?: string) => capitalizeFirstLetter(disappearPluralByValue(value));

export const CancelAutoRenewConfirmDialogVariants: Variants = {
    default: defaultVariant,
    autoUploadUnsubs: ({ value }) => ({
        image: defaultCoverImage,
        title: 'Отключить подписку?',
        subtitle: 'Без неё у вас:',
        body: [
            `${disappearPluralByValueCapitalize(value)} ${value} и безлимитная автозагрузка`,
            'Не получится загружать тяжёлые файлы',
            'Вернётся реклама — и в Почте тоже',
        ],
    }),
    trialExpiryReminder: ({ daysLeft, value }) => ({
        image: trialExpiryCoverImage,
        title:
            daysLeft && daysLeft > 0
                ? `У вас ещё ${daysLeft} ${daysNominativePlural(daysLeft)} бесплатной подписки. Отключить её?`
                : 'Вы уверены?',
        subtitle: `Что вы потеряете, если отключите автоплатёж:`,
        body: [
            `${disappearPluralByValueCapitalize(value)} ${value}`,
            'Не загрузятся файлы больше 1 ГБ',
            'Вернётся реклама в Облаке и Почте',
        ],
    }),
    vkMusic: ({ value }) => ({
        image: vkMusicOutflowImage,
        title: 'Если\u00A0отключить автоплатёж\u00A0— пропадут сразу два\u00A0сервиса',
        subtitle: '',
        body: `Вернётся реклама, ${disappearPluralV2ByValue(
            value
        )} ${value}. Нельзя будет слушать любимые треки с\u00A0выключенным экраном`,
    }),
    updatedUnsubScreens: ({ value, features }) => ({
        image: '',
        title: 'Без\u00A0автоплатежа подписка\nне\u00A0продлится',
        subtitle: 'Пропадут преимущества:',
        body: [
            `${value} дополнительного места`,
            features?.unlimitedUpload ? 'Безлимитная автозагрузка фото и\u00A0видео с телефона' : '',
            features?.disableAds ? 'Отключение рекламы в Почте и Облаке' : '',
            'Загрузка файлов и отправка вложений до 100 ГБ',
            features?.autodelete ? 'Таймер для\u00A0автоудаления отправленых папок' : '',
            features?.themedPublic ? 'Выбор оформления совместных папок' : '',
            features?.publicPassword ? 'Пароль для\u00A0совместных папок в\u00A0веб\u00A0версии' : '',
            features?.family ? 'Возможность делиться памятью с\u00A03\u00A0участниками' : '',
            'Приоритетная поддержка',
        ],
    }),
};

export const CancelAutoRenewConfirmDialog = memo<CancelAutoRenewConfirmDialogProps>(
    ({ onClose, onAction, onSuccess, onShow, isTrial, isVkMusic, subscription, skipAnalytic = false }): ReactElement => {
        const sendAnalytics = useSendSubscriptionModalAnalytics(subscription);
        const daysLeft = Math.ceil((subscription.expires - Date.now()) / MILLISECONDS_IN_DAY);

        useEffect(() => {
            onShow?.();
            if (!skipAnalytic) {
                emitAnalyticEvent(AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY_SHOW, { isTrial });
                sendAnalytics(ESubscriptionModalAction.showCancelAutopayment);
            }
        }, [isTrial, onShow, skipAnalytic]);

        const features = useGetPaidFeatures();

        let variant: Variant;
        if (isVkMusic) {
            variant = CancelAutoRenewConfirmDialogVariants.vkMusic;
        } else if (updatedUnsubScreensEnabled) {
            variant = CancelAutoRenewConfirmDialogVariants.updatedUnsubScreens;
        } else if (isTrial && trialExpiryReminder) {
            variant = CancelAutoRenewConfirmDialogVariants.trialExpiryReminder;
        } else if (autoUploadUnsubs && subscription?.space?.original >= SPACE_SIZES._256) {
            variant = CancelAutoRenewConfirmDialogVariants.autoUploadUnsubs;
        } else {
            variant = (params) => CancelAutoRenewConfirmDialogVariants.default({ ...params, isTrial });
        }

        useEffect(() => {
            if (variant === CancelAutoRenewConfirmDialogVariants.autoUploadUnsubs) {
                sendXray('autoupload_cancel_desktop_show');
            }
        }, [variant]);

        const { image, title } = variant({ daysLeft });

        const content = useMemo((): ReactElement => {
            const { subtitle, body } = variant({ value: subscription.space.value, features });

            const bodyElement =
                typeof body === 'string' ? (
                    <>
                        {body}
                        <Spacing size={4} />
                    </>
                ) : (
                    <ul className={styles.list}>
                        {body.map((value, index) => {
                            const isAd = value.toLowerCase().includes('реклама');

                            if (value && (!isAd || subscription.disableAds)) {
                                return <li key={index}>{value}</li>;
                            }

                            return null;
                        })}
                    </ul>
                );

            return (
                <div className={styles.content}>
                    <Spacing size={16} />
                    {subtitle && (
                        <>
                            {subtitle}
                            <Spacing size={12} />
                        </>
                    )}
                    {bodyElement}
                    <Spacing size={32} />
                </div>
            );
        }, [isTrial, subscription.space.value]);

        return (
            <WhatsNewDialog
                className={classNames(styles.root, { [styles.compact]: isVkMusic })}
                onClose={onClose}
                onClick={onSuccess}
                onSecondary={onAction}
                primaryButtonText={'Сохранить подписку'}
                secondaryButtonText="Отключить"
                title={title}
                content={content}
                imageUrl={image}
                imageBgColor="var(--vkui--color_background)"
                buttonTheme="vk"
                dialogSize="tiny"
                isRebranding
                hideImage={!subscription.disableAds || (updatedUnsubScreensEnabled && !isVkMusic)}
                titleClassName={updatedUnsubScreensEnabled ? styles.preline : undefined}
            />
        );
    }
);

CancelAutoRenewConfirmDialog.displayName = 'CancelAutoRenewConfirmDialog';
